import { useEffect, useState, useRef, useMemo } from "react";
import { Command, CommandInput, CommandList, CommandItem } from "cmdk";
import { cn } from "@/lib/utils";

export interface Option {
  value: string;
  label: string;
}

interface AutoCompleteProps {
  options: Option[];
  value?: Option | undefined;
  onValueChange: (option: Option | null) => void;
  onInputChange: (inputValue: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  allowCustomValue?: boolean;
  className?: string;
  autoFocus?: boolean;
}

export function AutoComplete({
  options,
  value,
  onValueChange,
  onInputChange,
  isLoading = false,
  placeholder = "Search...",
  className,
  autoFocus = false,
}: AutoCompleteProps) {
  const [query, setQuery] = useState(value?.label || "");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync input from selected value only when value is defined (user selected an option).
  // When value is undefined, the input is user-driven (they're typing) — don't overwrite.
  useEffect(() => {
    if (value !== undefined && value !== null && value.label !== query) {
      setQuery(value.label);
    }
  }, [value, query]);

  useEffect(() => {
    if (autoFocus && containerRef.current) {
      // Small delay to ensure the dialog is fully rendered
      setTimeout(() => {
        const input = containerRef.current?.querySelector(
          'input[type="text"]',
        ) as HTMLInputElement;
        input?.focus();
      }, 100);
    }
  }, [autoFocus]);

  const handleValueChange = (selectedValue: string) => {
    // cmdk passes the value prop to onSelect, which is now the unique ID
    const option = options.find((opt) => opt.value === selectedValue);
    if (option) {
      onValueChange(option);
      setQuery(option.label);
      setIsOpen(false);
    }
  };

  const handleInputChange = (inputValue: string) => {
    setQuery(inputValue);
    onInputChange(inputValue);
    if (!inputValue) {
      onValueChange(null);
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Filter options based on query
  const filteredOptions = useMemo(() => {
    if (!query.trim()) {
      return options;
    }
    const queryLower = query.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(queryLower),
    );
  }, [options, query]);

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <Command
        className="w-full rounded-lg overflow-visible"
        shouldFilter={false}
      >
        <CommandInput
          placeholder={placeholder}
          value={query}
          onValueChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.trim()) {
              setIsOpen(true);
            }
          }}
          onBlur={() => {
            // Delay closing to allow click events to fire
            setTimeout(() => {
              if (
                containerRef.current &&
                !containerRef.current.contains(document.activeElement)
              ) {
                setIsOpen(false);
              }
            }, 200);
          }}
          className="w-full h-10 px-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {isOpen && (filteredOptions.length > 0 || isLoading) && (
          <CommandList className="absolute z-50 w-full mt-1 bg-popover text-popover-foreground border border-border rounded-lg shadow-lg max-h-[300px] overflow-auto scrollbar-styled">
            {isLoading && (
              <CommandItem
                disabled
                className="px-4 py-2 text-sm text-muted-foreground cursor-not-allowed"
              >
                Loading…
              </CommandItem>
            )}
            {filteredOptions.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                onSelect={handleValueChange}
                className="px-4 py-2 text-sm text-foreground cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                {option.label}
              </CommandItem>
            ))}
          </CommandList>
        )}
      </Command>
    </div>
  );
}
